import { FC, useMemo, useRef, useState } from 'react';
import { captureException } from '@sentry/react';
import { Avatar, Flex, Skeleton, Typography } from 'antd';
import { AxiosResponse } from 'axios';
import { TbBuilding, TbExclamationCircle, TbSearch } from 'react-icons/tb';
import { useDebounce } from 'react-use';

import { authorizedAxiosInstance } from '@/clients';
import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import {
    CompanySearchResult,
    companySearchResultToSubjectReference,
    getCompanySearchResultExchange,
    getCompanySearchResultName,
    groupCompanySearchResults,
} from '@/shared/components/company-search/utils';
import { PlatformSelect } from '@/shared/components/forms';
import { EmptyView } from '@/shared/components/layouts/views/EmptySection';
import { SubjectReference } from '@/types';
import { corporateLogo } from '@/utilities';

interface WatchlistCompanySelectProps {
    disabled?: boolean;
    value: SubjectReference[];
    onChange: (subjects: SubjectReference[]) => void;
}

interface CompanySelectOption {
    label: string;
    result?: CompanySearchResult;
    subject: SubjectReference;
    value: string;
}

const subjectKey = ({ subjectId, subjectType }: SubjectReference) => `${subjectType}:${subjectId}`;

const resultWebsite = (result?: CompanySearchResult) =>
    result?.enrichment?.website ?? result?.website ?? result?.enrichment?.clearbit?.domain;

const WatchlistCompanySelect: FC<WatchlistCompanySelectProps> = ({ disabled, onChange, value }): JSX.Element => {
    const { token } = useAntDesignTheme();
    const requestId = useRef(0);
    const [error, setError] = useState(false);
    const [loading, setLoading] = useState(false);
    const [query, setQuery] = useState('');
    const [results, setResults] = useState<CompanySearchResult[]>([]);

    useDebounce(
        () => {
            const fragment = query.trim();

            if (fragment.length < 2) {
                requestId.current += 1;
                setError(false);
                setLoading(false);
                setResults([]);
                return;
            }

            const activeRequestId = ++requestId.current;
            setError(false);
            setLoading(true);

            void authorizedAxiosInstance
                .post<CompanySearchResult[]>('/api/search/vendors/fmp/companies', { fragment })
                .then((response: AxiosResponse<CompanySearchResult[]>) => {
                    if (requestId.current === activeRequestId) {
                        setResults(response.data ?? []);
                    }
                })
                .catch((searchError: unknown) => {
                    captureException(searchError);
                    if (requestId.current === activeRequestId) {
                        setError(true);
                        setResults([]);
                    }
                })
                .finally(() => {
                    if (requestId.current === activeRequestId) {
                        setLoading(false);
                    }
                });
        },
        450,
        [query],
    );

    const options = useMemo<CompanySelectOption[]>(() => {
        const selectedOptions = value.map((subject) => ({
            label: subject.subjectLabel ?? subject.subjectId,
            subject,
            value: subjectKey(subject),
        }));
        const resultOptions = groupCompanySearchResults(results)
            .flatMap((group) => [group.primary, ...group.alternatives])
            .flatMap((result) => {
                const subject = companySearchResultToSubjectReference(result);

                return subject
                    ? [
                          {
                              label: getCompanySearchResultName(result),
                              result,
                              subject,
                              value: subjectKey(subject),
                          },
                      ]
                    : [];
            });

        return Array.from(
            new Map([...selectedOptions, ...resultOptions].map((option) => [option.value, option])).values(),
        );
    }, [results, value]);

    const optionByValue = useMemo(() => new Map(options.map((option) => [option.value, option])), [options]);

    return (
        <div
            style={{
                border: `1px dashed ${token.colorBorder}`,
                borderRadius: token.borderRadiusLG,
                minHeight: value.length ? undefined : '13rem',
                padding: token.paddingSM,
            }}
        >
            <PlatformSelect<string[], CompanySelectOption>
                allowClear
                autoClearSearchValue
                disabled={disabled}
                filterOption={false}
                loading={loading}
                maxTagCount='responsive'
                mode='multiple'
                notFoundContent={
                    loading ? (
                        <Skeleton active paragraph={{ rows: 2 }} title={false} />
                    ) : (
                        <EmptyView
                            description={
                                error
                                    ? 'Company search is temporarily unavailable. Close the dropdown and try again.'
                                    : query.trim().length < 2
                                      ? 'Type at least two characters to search public and private company listings.'
                                      : 'Try another company name, ticker, or website.'
                            }
                            height='9rem'
                            icon={
                                error ? (
                                    <TbExclamationCircle size='1.25rem' />
                                ) : query.trim().length < 2 ? (
                                    <TbSearch size='1.25rem' />
                                ) : (
                                    <TbBuilding size='1.25rem' />
                                )
                            }
                            label={
                                error
                                    ? 'Unable to Search Companies'
                                    : query.trim().length < 2
                                      ? 'Search for Companies'
                                      : 'No Companies Found'
                            }
                        />
                    )
                }
                optionFilterProp='label'
                options={options}
                optionRender={(option) => {
                    const result = option.data.result;
                    const website = resultWebsite(result);
                    const symbol = result?.symbol ?? result?.ticker ?? result?.enrichment?.symbol;
                    const exchange = result ? getCompanySearchResultExchange(result) : undefined;

                    return (
                        <Flex align='center' gap='0.5rem' style={{ minWidth: 0, paddingBlock: token.paddingXXS }}>
                            <Avatar
                                icon={<TbBuilding size='0.875rem' />}
                                shape='square'
                                size='small'
                                src={website ? corporateLogo(website) : undefined}
                                style={{ border: `1px solid ${token.colorBorder}`, flex: '0 0 auto' }}
                            />
                            <div style={{ flex: '1 1 auto', minWidth: 0 }}>
                                <Typography.Text ellipsis style={{ display: 'block' }}>
                                    {option.data.label}
                                </Typography.Text>
                                {symbol ? (
                                    <small style={{ color: token.colorTextTertiary }}>
                                        {exchange && exchange !== 'Unknown Exchange' ? `${exchange}: ` : ''}
                                        {symbol}
                                    </small>
                                ) : null}
                            </div>
                        </Flex>
                    );
                }}
                placeholder='Search Companies By Name Or Ticker...'
                searchValue={query}
                showSearch
                style={{ width: '100%' }}
                suffixIcon={<TbSearch size='1rem' style={{ color: token.colorTextTertiary }} />}
                value={value.map(subjectKey)}
                onChange={(selectedValues) => {
                    onChange(
                        selectedValues.flatMap((selectedValue) => {
                            const option = optionByValue.get(selectedValue);
                            return option ? [option.subject] : [];
                        }),
                    );
                    requestId.current += 1;
                    setQuery('');
                    setLoading(false);
                    setResults([]);
                }}
                onDropdownVisibleChange={(open) => {
                    if (!open) {
                        requestId.current += 1;
                        setQuery('');
                        setLoading(false);
                        setResults([]);
                    }
                }}
                onSearch={(nextQuery) => {
                    setQuery(nextQuery);
                    if (nextQuery.trim().length >= 2) {
                        setLoading(true);
                    } else {
                        requestId.current += 1;
                        setLoading(false);
                        setResults([]);
                    }
                }}
            />
            {!value.length ? (
                <EmptyView
                    description='Search by company name or ticker, then choose one or more companies to add when the watchlist is created.'
                    height='9rem'
                    icon={<TbBuilding size='1.25rem' />}
                    label='No Companies Selected'
                />
            ) : null}
        </div>
    );
};

export default WatchlistCompanySelect;
